/**
 * Departure point lookups.
 *
 * Two requests, deliberately separate. The search reads what is already stored
 * and is safe to run while the member types; the lookup may reach a vendor for a
 * place nobody has used before, so the member triggers it explicitly.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  DepartureAnchorDto,
  DepartureAnchorLookupResponse,
  DepartureAnchorSearchResponse,
} from '@shared/types'

import { apiRequest } from '../lib/api'
import { queryKeys } from '../lib/query'
import { selectIsAuthenticated, useSessionStore } from '../stores/session'

/** Stored departure points matching the query, plus the caller's shortlist. */
export function useDepartureAnchors(query: string) {
  const isAuthenticated = useSessionStore(selectIsAuthenticated)

  return useQuery({
    queryKey: queryKeys.departureAnchors(query),
    enabled: isAuthenticated,
    // A stored anchor changes only when somebody resolves a new place, and the
    // picker is one screen, so a short stale window is enough.
    staleTime: 60_000,
    queryFn: () =>
      apiRequest<DepartureAnchorSearchResponse>(`/api/anchors?q=${encodeURIComponent(query)}`),
  })
}

/** Resolves a typed place, storing what it finds for the next member. */
export function useDepartureLookup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (query: string) =>
      apiRequest<DepartureAnchorLookupResponse>('/api/anchors/lookup', {
        method: 'POST',
        body: { query },
      }),
    onSuccess: () => {
      // The lookup just stored a place, so every cached search that could match
      // it - the picker's own query included - is out of date.
      void queryClient.invalidateQueries({ queryKey: queryKeys.anchors })
    },
  })
}

/** Re-exports the shape the picker renders. */
export type { DepartureAnchorDto }
